import { FiBook } from 'react-icons/fi';

export const awlSublist8Data = {
  courseId: 'awl-sublist-8',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 8',
  icon: FiBook,
  overview: {
    impact: 'Add high-band academic nuance for contrast, emphasis, and cautious evaluation',
    focus: 'clarify, contradict, highlight — plus ambiguous, bias, fluctuate, implicit',
    uses: 'Task 1 (fluctuate, highlight); Task 2 and Reading when claims contradict, need clarifying, or hide bias',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word meanings and usage',
      content: `<p>Sublist 8 is less frequent than earlier AWL bands, which is exactly why a few well-placed words can lift Lexical Resource. These headwords help you talk about unclear language (<em>ambiguous</em>, <em>implicit</em>), unfair slant (<em>bias</em>), and movement that is not a simple rise or fall (<em>fluctuate</em>). They also give you verbs for academic talk: <em>clarify</em>, <em>contradict</em>, <em>highlight</em>, <em>reinforce</em>.</p>
<p>The full Sublist 8 headword set in this course is: abandon, accompany, accumulate, ambiguous, appendix, appreciate, arbitrary, bias, clarify, complement, conform, contemporary, contradict, crucial, currency, denote, detect, deviate, displace, eventual, exhibit, exploit, fluctuate, guideline, highlight, implicit, induce, inspect, intense, manipulate, offset, paragraph, practitioner, predominant, prospect, radical, random, reinforce, restore, revise, terminate, theme, thereby, via, virtual, widespread.</p>
<p>The three focus verbs do different jobs. <strong>Clarify</strong> makes meaning clearer. <strong>Contradict</strong> says two claims cannot both be true. <strong>Highlight</strong> points the reader at the important change — the graph will highlight the most important change, and a topic sentence can highlight a cause.</p>`,
      points: [
        'These words distinguish careful Band 7–8 writing from generic Band 6 vocabulary.',
        'Fluctuate, offset, and widespread are especially useful in Task 1.',
        'Bias, arbitrary, and implicit are useful when you evaluate research or media.',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['abandon', 'To give up completely; to leave behind', 'The city abandoned the motorway plan after public protests.'],
          ['ambiguous', 'Open to more than one interpretation', 'The wording of the rule was deliberately ambiguous.'],
          ['arbitrary', 'Based on random choice rather than a fair system', 'An arbitrary age limit is hard to defend.'],
          ['bias', 'An inclination for or against something', 'The sample showed a clear bias towards urban respondents.'],
          ['clarify', 'To make a statement easier to understand', 'Could you clarify whether the fee is annual or monthly?'],
          ['contradict', 'To be in conflict with; to say the opposite', 'Later figures contradict the government’s early claim.'],
          ['crucial', 'Extremely important; decisive', 'Teacher training is crucial if the reform is to succeed.'],
          ['fluctuate', 'To rise and fall irregularly', 'Oil prices fluctuated throughout the decade.'],
          ['highlight', 'To draw special attention to', 'The graph will highlight the most important change.'],
          ['implicit', 'Suggested but not stated directly', 'The advert makes an implicit claim that thinner is healthier.'],
          ['reinforce', 'To strengthen an idea, structure, or behaviour', 'Daily practice reinforces the new collocations.'],
          ['widespread', 'Found or happening over a large area or among many people', 'Smartphone use is now widespread even in rural towns.'],
        ],
      },
    },
    {
      title: 'Word families',
      content: `<p>Negative and opposite forms are part of the value of this list. <em>Ambiguous</em> / <em>unambiguous</em> / <em>ambiguity</em>. <em>Bias</em> / <em>biased</em> / <em>unbiased</em>. <em>Contradict</em> / <em>contradiction</em> / <em>contradictory</em>. Using the adjective in a noun slot (“a contradict”) is a typical slip.</p>
<p>Some items look like everyday words but have academic senses. <em>Appreciate</em> can mean understand the scale of a problem, not only “be grateful.” <em>Currency</em> can mean money in circulation or the state of being current. <em>Complement</em> (complete) is not <em>compliment</em> (praise). <em>Appendix</em> in a report is not the body organ — though the spelling is shared.</p>`,
      points: [
        'Highlight → highlighted / highlighting; highlighter is a pen, not the academic verb form you want in Task 1.',
        'Fluctuate → fluctuation / fluctuating — Task 1 loves the noun “fluctuations.”',
        'Thereby is a formal linker meaning “by that means”; do not confuse it with therefore.',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['abandon', 'abandonment', 'abandon', 'abandoned'],
          ['ambiguous', 'ambiguity', '—', 'ambiguous, ambiguously, unambiguous'],
          ['arbitrary', 'arbitrariness', '—', 'arbitrary, arbitrarily'],
          ['bias', 'bias', 'bias', 'biased, unbiased'],
          ['clarify', 'clarification', 'clarify', 'clarified'],
          ['contradict', 'contradiction', 'contradict', 'contradictory'],
          ['fluctuate', 'fluctuation', 'fluctuate', 'fluctuating'],
          ['highlight', 'highlight', 'highlight', 'highlighted'],
          ['implicit', '—', '—', 'implicit, implicitly'],
          ['reinforce', 'reinforcement', 'reinforce', 'reinforced'],
          ['widespread', '—', '—', 'widespread'],
          ['crucial', '—', '—', 'crucial, crucially'],
        ],
      },
    },
    {
      title: 'Common collocations',
      content: `<p>Collocations stop advanced words from sounding ornamental. “Highlight a change,” “contradict a claim,” “clarify a point,” “accumulate evidence,” and “widespread support” are natural. “Do a highlight” is not.</p>
<p>Task 1 set: <em>fluctuate between</em>, <em>offset a decline</em>, <em>a sharp fluctuation</em>. Task 2 set: <em>abandon a plan</em>, <em>conform to standards</em>, <em>a radical proposal</em>, <em>the prospect of</em>, <em>via public transport</em>. Evaluation set: <em>cultural bias</em>, <em>implicit assumption</em>, <em>arbitrary decision</em>, <em>ambiguous wording</em>.</p>`,
      points: [
        'Highlight is the academic verb for “show the important part.”',
        'Contradict needs an object: a claim, a finding, or an earlier statement.',
      ],
      examples: [
        { label: 'highlight', text: 'highlight the change, highlight a problem, the highlight of' },
        { label: 'contradict', text: 'contradict a claim, apparently contradictory, in contradiction to' },
        { label: 'clarify', text: 'clarify a point, seek clarification, clarify whether' },
        { label: 'fluctuate', text: 'fluctuate between, wild fluctuations, fluctuate over time' },
        { label: 'bias', text: 'confirmation bias, gender bias, biased towards, unbiased sample' },
        { label: 'ambiguous', text: 'highly ambiguous, ambiguous wording, moral ambiguity' },
        { label: 'implicit', text: 'implicit assumption, implicit in, implicitly accept' },
        { label: 'widespread', text: 'widespread support, widespread use, become widespread' },
        { label: 'abandon', text: 'abandon a plan, abandon the attempt, abandoned building' },
        { label: 'reinforce', text: 'reinforce an argument, reinforce a stereotype, reinforce a habit' },
      ],
    },
    {
      title: 'Using Sublist 8 in IELTS tasks',
      content: `<p>In Task 1, one precise verb beats three vague ones: “Sales fluctuated, whereas profits remained stable.” Use <em>highlight</em> in the overview if one year or category is the story. In Task 2, <em>contradict</em> is stronger than “is different from” when two official claims cannot both stand. <em>Clarify</em> is useful in speaking if the examiner’s question has two possible readings — that is native-like, not weak.</p>
<p>Be careful with loaded verbs. <em>Exploit</em> can mean use unfairly or use a resource; add a fair object (“exploit renewable sources”) or an unfair one (“exploit workers”) so the stance is clear. <em>Manipulate</em> is usually negative. <em>Radical</em> should describe a deep change, not every new app.</p>`,
      points: [
        'Use fluctuate only when the line goes up and down, not for a single fall.',
        'Use implicit for unstated meaning and explicit (Sublist 6) for stated meaning.',
        'Via means “by way of” — via email, via a tunnel — not a synonym of because.',
      ],
      examples: [
        { label: 'Weak sentence', text: 'The graph will show the most important change.' },
        { label: 'Strong sentence', text: 'The graph will highlight the most important change.' },
        { label: 'Weak sentence', text: 'The new numbers say the opposite of the first announcement.' },
        { label: 'Strong sentence', text: 'Later figures contradict the government’s early claim.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match word to definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        {
          text: 'What does “highlight” mean as a verb?',
          options: ['To draw special attention to', 'To hide a problem', 'To measure in kilograms'],
          answer: 'To draw special attention to',
          explanation: 'Highlight means make the important part stand out — in a graph, an essay, or a talk. It is not a unit of weight.',
        },
        {
          text: 'What does “contradict” mean?',
          options: ['To support a claim with extra examples', 'To be in conflict with; to say the opposite', 'To summarise a paragraph'],
          answer: 'To be in conflict with; to say the opposite',
          explanation: 'If later data contradict a claim, the two cannot both be true. Contradict is stronger than “differ from.”',
        },
        {
          text: 'What does “ambiguous” mean?',
          options: ['Open to more than one interpretation', 'Perfectly clear to everyone', 'Written only in numbers'],
          answer: 'Open to more than one interpretation',
          explanation: 'Ambiguous language can be read in two ways. The opposite idea is unambiguous or explicit.',
        },
        {
          text: 'What does “fluctuate” mean?',
          options: ['To rise and fall irregularly', 'To rise in a straight line forever', 'To disappear completely'],
          answer: 'To rise and fall irregularly',
          explanation: 'Fluctuate is the Task 1 verb for an unstable line. A single increase is not a fluctuation.',
        },
        {
          text: 'What does “implicit” mean?',
          options: ['Suggested but not stated directly', 'Printed in bold type', 'Officially banned'],
          answer: 'Suggested but not stated directly',
          explanation: 'An implicit claim is hinted. IELTS Reading often tests whether you notice what is implied rather than said.',
        },
      ],
    },
    {
      title: 'Complete the sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct Sublist 8 word.',
      questions: [
        {
          text: 'The graph will ____ the most important change.',
          answer: 'highlight',
          explanation: 'After “will” use the base verb. Highlight is the academic choice for drawing attention to a key feature.',
        },
        {
          text: 'Later surveys ____ the minister’s claim that crime had vanished.',
          answer: 'contradict',
          explanation: 'Contradict takes a claim as its object. The new surveys say the opposite of the claim.',
        },
        {
          text: 'Oil prices ____ between $40 and $90 rather than rising steadily.',
          answer: 'fluctuated',
          explanation: 'The past simple fluctuated fits a finished period. The meaning is irregular rise and fall, which “between” supports.',
        },
        {
          text: 'The contract was ____, so both sides interpreted the fee differently.',
          answer: 'ambiguous',
          explanation: 'Ambiguous means two readings are possible — exactly why the two sides disagreed.',
        },
      ],
    },
    {
      title: 'Choose the correct word form',
      type: 'select',
      instructions: 'Select the grammatically correct form for each sentence.',
      questions: [
        {
          text: 'The graph will ____ the most important change. (highlight)',
          options: ['highlight', 'highlighting', 'highlighter'],
          answer: 'highlight',
          explanation: '“Will” needs the base verb. Highlighting is a participle; highlighter is a physical pen.',
        },
        {
          text: 'There is a clear ____ between the two official reports. (contradict)',
          options: ['contradict', 'contradiction', 'contradictory'],
          answer: 'contradiction',
          explanation: 'After “a clear” you need a noun. Contradiction names the conflict; contradictory would need a noun after it.',
        },
        {
          text: 'The sample was ____ towards older voters. (bias)',
          options: ['bias', 'biased', 'unbiased'],
          answer: 'biased',
          explanation: 'After “was” you need an adjective. Biased towards is the collocation. Unbiased would reverse the meaning.',
        },
        {
          text: 'The figures show wide ____ across the year. (fluctuate)',
          options: ['fluctuate', 'fluctuation', 'fluctuations'],
          answer: 'fluctuations',
          explanation: 'After “wide” a plural noun is natural because a year contains many movements. Fluctuate is a verb.',
        },
      ],
    },
  ],
  tips: [
    'Use highlight for emphasis, clarify for meaning, and contradict for logical conflict — three different jobs.',
    'Fluctuate is for up-and-down movement, not for one downward year.',
    'Complement (complete) is not compliment (praise); this pair is a common spelling trap.',
    'Thereby means “by doing that,” therefore means “for that reason.”',
  ],
  dailyPractice: [
    'Write a Task 1 overview that uses fluctuate or highlight once — not both in every sentence.',
    'Take two news headlines that disagree and join them with contradict.',
    'Rewrite “the rule is unclear” using ambiguous or implicit.',
    'Make a mini word-family card for bias, contradict, and fluctuate.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 8 vocabulary course covering Coxhead headwords such as abandon, ambiguous, arbitrary, bias, clarify, contradict, crucial, fluctuate, highlight, implicit, reinforce, and widespread. Includes the full stub list, word families, collocations, and IELTS Task 1/Task 2 uses for nuance and evaluation.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist8Data,
      title: awlSublist8Data.title + ' (Foundations)',
      overview: { ...awlSublist8Data.overview, duration: '15-20 minutes' },
      sections: awlSublist8Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist8Data.sections.length / 2))),
      exercises: awlSublist8Data.exercises.slice(0, 1).map((ex) => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist8Data,
      title: awlSublist8Data.title + ' (Advanced)',
      overview: { ...awlSublist8Data.overview, duration: '30-35 minutes' },
      sections: awlSublist8Data.sections,
      exercises: awlSublist8Data.exercises.map((ex) => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist8Data.tips || []),
        'For Band 8+: use implicit and contradict together when a slogan hides a second message.',
        'Advanced tip: offset a cost in the same sentence that you highlight a benefit.',
      ],
    };
  }
  return awlSublist8Data;
}
